import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import axiosInstance from "../../../hooks/api";
import { useFormSubmit } from "../../../hooks/useFormSubmit";
import CopyFromPanel from "./sacrament/CopyFromPanel";
import { BAPTISM_COPY_SECTIONS } from "../../../helper/copyFormSections";

const NasBaptismForm = ({
  birthDate,
  onSuccess,
  multiFlow = false,
  isFinalStep = false,
  onContinue,
  onSubmitBatch,
  isBatchLoading = false,
  copySources = [],
}) => {
  const { t } = useTranslation();
  const { isLoading, submit } = useFormSubmit();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ mode: "onTouched" });

  useEffect(() => {
    setValue("birth_date", birthDate || "", { shouldValidate: !!birthDate });
  }, [birthDate, setValue]);

  const busy = isLoading || isBatchLoading;

  const onSubmit = async (data) => {
    if (multiFlow) {
      const draft = { kind: "infant_baptism", birthDate, data };
      if (!isFinalStep) {
        if (onContinue) onContinue(draft);
        return;
      }
      if (onSubmitBatch) await onSubmitBatch(draft);
      return;
    }

    const baptismRequestPayload = {
      birth_date: data.birth_date || "",
      child_name: data.child_name,
      birth_place: data.birth_place,
      mother_name: data.mother_name,
      mother_phone: data.mother_phone,
      mother_email: data.mother_email,
      father_name: data.father_name,
      father_phone: data.father_phone,
      father_email: data.father_email,
      address_line_1: data.address_line_1,
      city: data.city,
      state: data.state,
      zip_code: data.zip_code,
      godfather_name: data.godfather_name,
      godfather_religion: data.godfather_religion,
      godfather_phone: data.godfather_phone,
      godfather_email: data.godfather_email,
      godmother_name: data.godmother_name,
      godmother_religion: data.godmother_religion,
      godmother_phone: data.godmother_phone,
      godmother_email: data.godmother_email,
      comments: data.comments || null,
    };

    await submit(
      () => axiosInstance.post("/public/baptism", baptismRequestPayload),
      {
        successText: t("_baptism_request_confirmation"),
        onSuccess: () => {
          reset();
          if (onSuccess) onSuccess();
        },
      }
    );
  };

  const submitLabel = multiFlow
    ? (isFinalStep ? t("_submit_all_registrations") : t("_continue_next_person"))
    : t("_register_for_baptism");


  return (
    <div className="section pt-0">
      <div className="container">
        <div className="section-title text-center">
          <h6 className="subtitle cursive-style">{t("_baptism_request_form")}</h6>
          <p>{t("_donation_message")}</p>
        </div>
        <div className="sigma_contact-wrap">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="sigma_contact-form p-4" style={{ background: "#fff", borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
                <form onSubmit={handleSubmit(onSubmit)}>

                  {multiFlow && (
                    <CopyFromPanel
                      sources={copySources}
                      sections={BAPTISM_COPY_SECTIONS}
                      setValue={setValue}
                    />
                  )}

                  {/* Notice */}
                  <div className="alert" style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "6px", padding: "10px 16px", marginBottom: "16px" }}>
                    <em className="text-danger fw-bold">* {t("_bring_birth_cert")}</em>
                  </div>

                  {/* Section: Child Info */}
                  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65" }}>
                    {t('_section_child_info')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_child_full_name")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.child_name ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("child_name", { required: t("_err_required") })}
                      />
                      {errors.child_name && <div className="invalid-feedback">{errors.child_name.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_birth_date")} <em className="text-danger">*</em></label>
                      <input
                        type="hidden"
                        {...register("birth_date", { required: t("_err_required") })}
                      />
                      <input
                        type="text"
                        className="form-control border"
                        value={(() => {
                          if (!birthDate) return "";
                          const [yyyy, mm, dd] = birthDate.split("-");
                          return `${mm}/${dd}/${yyyy}`;
                        })()}
                        disabled
                        readOnly
                        title="Este campo se llena automáticamente con la fecha de nacimiento ingresada arriba."
                        style={{ backgroundColor: "#f5f5f5", color: "#555", cursor: "not-allowed" }}
                      />
                      {errors.birth_date && (
                        <div className="invalid-feedback d-block">{errors.birth_date.message}</div>
                      )}
                    </div>
                    <div className="col-sm-12">
                      <label className="form-label fw-semibold">{t("_birth_place")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.birth_place ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("birth_place", { required: t("_err_required") })}
                      />
                      {errors.birth_place && <div className="invalid-feedback">{errors.birth_place.message}</div>}
                    </div>
                  </div>

                  {/* Section: Mother */}
                  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65" }}>
                    {t('_section_mothers_info')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-12">
                      <label className="form-label fw-semibold">{t("_mother_full_name")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.mother_name ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("mother_name", { required: t("_err_required") })}
                      />
                      {errors.mother_name && <div className="invalid-feedback">{errors.mother_name.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_mother_phone")} <em className="text-danger">*</em></label>
                      <input
                        type="tel"
                        className={`form-control border ${errors.mother_phone ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("mother_phone", { required: t("_err_required") })}
                      />
                      {errors.mother_phone && <div className="invalid-feedback">{errors.mother_phone.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_mother_email")} <em className="text-danger">*</em></label>
                      <input
                        type="email"
                        className={`form-control border ${errors.mother_email ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("mother_email", {
                          required: t("_err_required"),
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
                        })}
                      />
                      {errors.mother_email && <div className="invalid-feedback">{errors.mother_email.message}</div>}
                    </div>
                  </div>

                  {/* Section: Father */}
                  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65" }}>
                    {t('_section_fathers_info')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-12">
                      <label className="form-label fw-semibold">{t("_father_full_name")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.father_name ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("father_name", { required: t("_err_required") })}
                      />
                      {errors.father_name && <div className="invalid-feedback">{errors.father_name.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_father_phone")} <em className="text-danger">*</em></label>
                      <input
                        type="tel"
                        className={`form-control border ${errors.father_phone ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("father_phone", { required: t("_err_required") })}
                      />
                      {errors.father_phone && <div className="invalid-feedback">{errors.father_phone.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_father_email")} <em className="text-danger">*</em></label>
                      <input
                        type="email"
                        className={`form-control border ${errors.father_email ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("father_email", {
                          required: t("_err_required"),
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
                        })}
                      />
                      {errors.father_email && <div className="invalid-feedback">{errors.father_email.message}</div>}
                    </div>
                  </div>

                  {/* Section: Address */}
                  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65" }}>
                    {t('_section_home_address')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-12">
                      <label className="form-label fw-semibold">{t("_address_1")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.address_line_1 ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("address_line_1", { required: t("_err_required") })}
                      />
                      {errors.address_line_1 && <div className="invalid-feedback">{errors.address_line_1.message}</div>}
                    </div>
                    <div className="col-sm-5">
                      <label className="form-label fw-semibold">City <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.city ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("city", { required: t("_err_required") })}
                      />
                      {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
                    </div>
                    <div className="col-sm-3">
                      <label className="form-label fw-semibold">State <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.state ? "is-invalid" : ""}`}
                        maxLength={2}
                        style={{ textTransform: "uppercase", scrollMarginTop: "100px" }}
                        {...register("state", { required: t("_err_required") })}
                      />
                      {errors.state && <div className="invalid-feedback">{errors.state.message}</div>}
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label fw-semibold">Zip Code <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.zip_code ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("zip_code", { required: t("_err_required") })}
                      />
                      {errors.zip_code && <div className="invalid-feedback">{errors.zip_code.message}</div>}
                    </div>
                  </div>

                  {/* Section: Godfather */}
                  <div className="alert" style={{ background: "#e8f0fb", border: "1px solid #032C65", borderRadius: "6px", padding: "10px 16px", marginBottom: "16px" }}>
                    <em className="text-danger fw-bold">* {t("_godparents_religion_req")}</em>
                  </div>
                  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65" }}>
                    {t('_section_godfather')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-8">
                      <label className="form-label fw-semibold">{t("_godfather_full_name")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.godfather_name ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godfather_name", { required: t("_err_required") })}
                      />
                      {errors.godfather_name && <div className="invalid-feedback">{errors.godfather_name.message}</div>}
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label fw-semibold">{t("_godfather_religion")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.godfather_religion ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godfather_religion", { required: t("_err_required") })}
                      />
                      {errors.godfather_religion && <div className="invalid-feedback">{errors.godfather_religion.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_godfather_phone")} <em className="text-danger">*</em></label>
                      <input
                        type="tel"
                        className={`form-control border ${errors.godfather_phone ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godfather_phone", { required: t("_err_required") })}
                      />
                      {errors.godfather_phone && <div className="invalid-feedback">{errors.godfather_phone.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_godfather_email")} <em className="text-danger">*</em></label>
                      <input
                        type="email"
                        className={`form-control border ${errors.godfather_email ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godfather_email", {
                          required: t("_err_required"),
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
                        })}
                      />
                      {errors.godfather_email && <div className="invalid-feedback">{errors.godfather_email.message}</div>}
                    </div>
                  </div>

                  {/* Section: Godmother */}
                  <h6 style={{ borderBottom: "2px solid #032C65", paddingBottom: "6px", marginBottom: "16px", color: "#032C65" }}>
                    {t('_section_godmother')}
                  </h6>
                  <div className="row g-3 mb-4">
                    <div className="col-sm-8">
                      <label className="form-label fw-semibold">{t("_godmother_full_name")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.godmother_name ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godmother_name", { required: t("_err_required") })}
                      />
                      {errors.godmother_name && <div className="invalid-feedback">{errors.godmother_name.message}</div>}
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label fw-semibold">{t("_godmother_religion")} <em className="text-danger">*</em></label>
                      <input
                        type="text"
                        className={`form-control border ${errors.godmother_religion ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godmother_religion", { required: t("_err_required") })}
                      />
                      {errors.godmother_religion && <div className="invalid-feedback">{errors.godmother_religion.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_godmother_phone")} <em className="text-danger">*</em></label>
                      <input
                        type="tel"
                        className={`form-control border ${errors.godmother_phone ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godmother_phone", { required: t("_err_required") })}
                      />
                      {errors.godmother_phone && <div className="invalid-feedback">{errors.godmother_phone.message}</div>}
                    </div>
                    <div className="col-sm-6">
                      <label className="form-label fw-semibold">{t("_godmother_email")} <em className="text-danger">*</em></label>
                      <input
                        type="email"
                        className={`form-control border ${errors.godmother_email ? "is-invalid" : ""}`}
                        style={{ scrollMarginTop: "100px" }}
                        {...register("godmother_email", {
                          required: t("_err_required"),
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: t("_err_invalid_email") },
                        })}
                      />
                      {errors.godmother_email && <div className="invalid-feedback">{errors.godmother_email.message}</div>}
                    </div>
                  </div>

                  {/* Comments */}
                  <div className="mb-4">
                    <label className="form-label fw-semibold">{t("_comments")}</label>
                    <textarea className="form-control border" rows={3} {...register("comments")} />
                  </div>

                  <div className="d-grid">
                    <button
                      type="submit"
                      className={busy ? "sigma_btn-custom" : "sigma_btn-custom light"}
                      disabled={busy}
                      style={{ backgroundColor: busy ? "grey" : "", padding: "14px", fontSize: "16px" }}
                    >
                      {busy ? "..." : submitLabel}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NasBaptismForm;
